import { useState, useCallback } from 'react';
import { generateVerticalPDF, generateHorizontalPDF, generateCoverPDF as generateCoverPDFService } from '../services/pdfService';
import { generateLuluCoverPDF } from '../services/pdfService/luluCoverPDF';
import { uploadPDF } from '../services/firebase/storage';
import { updateBookPdfUrls, getBook } from '../services/bookService';

export function usePDFGeneration(book) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [savedPdfUrls, setSavedPdfUrls] = useState({
    cover: book?.pdfUrls?.cover || null,
    verticalContent: book?.pdfUrls?.verticalContent || null,
    horizontalContent: book?.pdfUrls?.horizontalContent || null
  });

  // Convert orientation to PDF type
  const orientationToPdfType = (orientation) => {
    switch (orientation) {
      case 'vertical':
        return 'verticalContent';
      case 'horizontal':
        return 'horizontalContent';
      case 'cover':
        return 'cover';
      default:
        return 'verticalContent';
    }
  };

  const generatePDF = useCallback(async (orientation = 'vertical', saveToStorage = false, options = {}) => {
    if (!book || isGenerating) return;

    try {
      setIsGenerating(true);
      setError(null);

      // FRESH FETCH: Get latest book data from Firestore before generating PDF
      // This ensures any backend-created image elements are included
      let freshBook = book;
      try {
        console.log('📚 Fetching fresh book data before PDF generation...');
        freshBook = await getBook(book.id);
        console.log('✓ Fresh book data loaded with spreads:', Object.keys(freshBook.spreads || {}));
      } catch (fetchErr) {
        console.warn('⚠️ Could not fetch fresh book data, using cached version:', fetchErr.message);
        // Fall back to cached book if fresh fetch fails
      }

      let pdfBlob;
      let generator;
      switch (orientation) {
        case 'vertical':
          generator = generateVerticalPDF;
          break;
        case 'horizontal':
          generator = generateHorizontalPDF;
          break;
        case 'cover':
          // Use specialized Lulu PDF generator when exactLuluDimensions is true
          if (options.exactLuluDimensions === true) {
            generator = generateLuluCoverPDF;
            console.log('📏 Using SPECIALIZED Lulu Cover PDF generator with EXACT dimensions');
          } else {
            generator = generateCoverPDFService;
            console.log('Using standard cover PDF generator');
          }
          break;
        default:
          throw new Error(`Unsupported orientation: ${orientation}`);
      }

      // Generate the PDF blob using FRESH book data
      pdfBlob = await generator(freshBook, options);

      // Save to Firebase Storage if requested
      if (saveToStorage && book.id) {
        const pdfType = orientationToPdfType(orientation);
        const { url, error: uploadError } = await uploadPDF(pdfBlob, book.id, pdfType);
        
        if (uploadError) {
          console.error('Error uploading PDF:', uploadError);
          setError(uploadError);
        } else if (url) {
          // Update the PDF URL in Firestore using our specialized function
          try {
            await updateBookPdfUrls(book.id, { [pdfType]: url });
            
            // Update local state
            setSavedPdfUrls(prev => ({
              ...prev,
              [pdfType]: url
            }));
            
            console.log(`${pdfType} PDF saved to Firebase Storage:`, url);
          } catch (dbError) {
            console.error('Error updating book with PDF URL:', dbError);
            setError(dbError.message);
          }
        }
      }

      // If caller wants the Blob directly (no upload, no download)
      if (options?.returnBlob) {
        return { blob: pdfBlob };
      }

      // Create download link for user if needed (no upload and no returnBlob)
      if (!saveToStorage && !options?.returnBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${book.title || 'book'}-${orientation}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      return true;
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(err.message);
      return false;
    } finally {
      setIsGenerating(false);
    }
  }, [book, isGenerating]);

  // Function to generate cover PDF
  const generateCoverPDF = useCallback(async (saveToStorage = false, options = {}) => {
    if (!book || isGenerating) return false;
    
    // If this is a Lulu cover PDF, log that explicitly
    if (options.forLuluPrinting) {
      console.log('📏 Generating LULU-SPECIFIC cover PDF with EXACT dimensions');
    }
    
    // Pass options to generatePDF
    return generatePDF('cover', saveToStorage, {
      ...options,
      forLuluPrinting: options.forLuluPrinting || false
    });
  }, [generatePDF, book, isGenerating]);

  // Function to generate interior PDF (horizontal without cover) for printing
  const generateInteriorPDF = useCallback(async (saveToStorage = false, options = {}) => {
    if (!book || isGenerating) return false;
    // Always skip the cover spread for interior PDFs
    return generatePDF('horizontal', saveToStorage, { 
      skipCoverSpread: true,
      ...options
    });
  }, [generatePDF, book, isGenerating]);

  // Function to generate vertical interior PDF (without cover pages) for printing
  const generateVerticalInteriorPDF = useCallback(async (saveToStorage = false, options = {}) => {
    if (!book || isGenerating) return false;
    // Always skip the cover pages for vertical interior PDFs and force exact 6x9 dimensions for Lulu when requested
    // Note: Even with skipCover:true, the PDF will include the title page as the first page
    return generatePDF('vertical', saveToStorage, { 
      skipCover: true,
      forLuluPrinting: options.forLuluPrinting || false,  // Use provided value or default to false
      ...options
    });
  }, [generatePDF, book, isGenerating]);

  return {
    generatePDF,
    generateCoverPDF,
    generateInteriorPDF,
    generateVerticalInteriorPDF,
    isGenerating,
    error,
    savedPdfUrls
  };
}